import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { handleKeycloakCallback } from '@/lib/keycloak';
import { apiClient } from '@/lib/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { applyThemeFromBackend } from '@/contexts/ThemeContext';
import { toast } from 'sonner';

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthUser } = useAuth();
  // isLoading starts true and is never set to false — the spinner is hidden only when
  // navigate() causes React Router to unmount this component on successful navigation.
  const [isLoading] = useState(true);
  const hasProcessed = useRef(false);
  
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const processCallback = async () => {
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      
      if (error) {
        toast.error('Authentication failed', {
          description: searchParams.get('error_description') || 'Please try again',
        });
        navigate('/');
        return;
      }
      
      if (!code) {
        toast.error('Authentication failed', {
          description: 'No authorization code received',
        });
        navigate('/');
        return;
      }
      
      try {
        const state = searchParams.get('state');

        // Exchange the authorization code for tokens
        const { accessToken, refreshToken, expiresIn, refreshExpiresIn } =
          await handleKeycloakCallback(code, state);

        // Authenticate with the backend — backend sets both HttpOnly cookies
        const { user } = await apiClient.authenticateWithKeycloak(
          accessToken,
          refreshToken,
          expiresIn,
          refreshExpiresIn,
        );

        // Set auth state directly from the response — no extra GET /api/Auth/me needed
        setAuthUser(user);

        // Apply user preferences immediately after login
        if (user.theme) {
          applyThemeFromBackend(user.theme);
        }
        if (user.language) {
          localStorage.setItem('veritas-language', user.language);
        }
        
        localStorage.removeItem('auth_redirect');
        
        toast.success('Welcome back!', {
          description: 'Successfully signed in',
        });
        
        navigate('/dashboard');
      } catch (error) {
        console.error('Authentication error:', error);
        toast.error('Authentication failed', {
          description: error instanceof Error ? error.message : 'Please try again',
        });
        navigate('/');
      }
    };
    
    processCallback();
  }, [searchParams, navigate, setAuthUser]);

  if (!isLoading) return null;

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-semibold">Completing authentication...</h2>
        <p className="text-muted-foreground mt-2">Please wait while we sign you in</p>
      </div>
    </div>
  );
};

export default AuthCallback;
